import {useState} from "react";
import moment from "moment";
import {useSelector} from "react-redux";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {useLocation, useNavigate} from "react-router-dom";

import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import InboxIcon from "@mui/icons-material/Inbox";
import TimelineIcon from "@mui/icons-material/Timeline";
import TodayIcon from "@mui/icons-material/Today";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckIcon from "@mui/icons-material/Check";
import HandshakeIcon from "@mui/icons-material/Handshake";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import SportsIcon from "@mui/icons-material/Sports";
import StadiumIcon from "@mui/icons-material/Stadium";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import WorkIcon from "@mui/icons-material/Work";

import "moment/locale/it";

import ChoiceChips from "@/components/ChoiceChips";
import DataTable from "@/components/DataTable";
import EmptyData from "@/components/EmptyData";
import Kpi, {KpiRow} from "@/components/Kpi";
import StatusBadge from "@/components/StatusBadge";

import {ownersFor} from "@/utils/clubControl";

import "./index.scss";

// Mattoncini UI della vista Direttore Sportivo: pochi elementi, sempre uguali.

export function DsHeader({
	title,
	subtitle,
	snapshot,
	onReload,
	reloading,
	actions,
	scope,
	area,
}) {
	return (
		<div className="ds-header">
			<div>
				<h1 className="ds-header__title">{title}</h1>
				{(subtitle || snapshot) && (
					<div className="ds-header__subtitle">
						{subtitle}
						{snapshot && (
							<>
								{subtitle && " · "}
								{snapshot.season?.name} · aggiornato alle{" "}
								{moment(snapshot.loadedAt).format("HH:mm")}
							</>
						)}
					</div>
				)}
				{area && <DsOwners owners={ownersFor(snapshot, area)} />}
			</div>
			<div className="ds-header__actions">
				{scope && <DsTeamSelect scope={scope} />}
				{actions}
				{onReload && (
					<button
						className={"ds-btn ds-btn--ghost" + (reloading ? " is-busy" : "")}
						onClick={onReload}
						disabled={reloading}
					>
						<RefreshIcon fontSize="small" className="ds-btn__spin" />
						{reloading ? "Aggiorno…" : "Aggiorna"}
					</button>
				)}
			</div>
		</div>
	);
}

const TONES = {green: "good", yellow: "warning", red: "critical"};

export const DsKpiRow = KpiRow;

export function DsKpi({label, value, sub, tone}) {
	return <Kpi label={label} value={value} note={sub} tone={TONES[tone]} />;
}

export function DsSection({title, hint, action, preview, children}) {
	return (
		<section className="ds-section">
			{(title || action) && (
				<div className="ds-section__head">
					<div>
						<h2 className="ds-section__title">
							{title}
							{preview && <DsPreviewBadge />}
						</h2>
						{hint && <div className="ds-section__hint">{hint}</div>}
					</div>
					{action}
				</div>
			)}
			{children}
		</section>
	);
}

export function DsGrid({children, min = 260}) {
	return (
		<div
			className="ds-grid"
			style={{gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`}}
		>
			{children}
		</div>
	);
}

const STATUS_TONES = {ok: "good", warn: "warning", alert: "critical"};

export function DsStatus({status = "none", label}) {
	if (status === "preview") {
		return (
			<span className="ds-status-preview">
				<span className="ds-status-preview__dot" />
				{label}
			</span>
		);
	}
	return <StatusBadge tone={STATUS_TONES[status] || "neutral"} label={label} />;
}

export function DsPreviewBadge() {
	return <span className="ds-preview-badge">Anteprima</span>;
}

export function DsPreviewNotice({children}) {
	return (
		<div className="ds-preview-notice">
			<DsPreviewBadge />
			<span>
				{children ||
					"Modulo non ancora attivo: i dati mostrati sono di esempio e servono a dare un'idea di come apparirà."}
			</span>
		</div>
	);
}

export const DsEmpty = EmptyData;

export function DsMissingSources({sources}) {
	if (!sources?.length) return null;
	return (
		<div className="ds-missing">
			Dati non disponibili al momento: {sources.join(", ")}. Le sintesi possono
			essere parziali.
		</div>
	);
}

// Le esclusioni dal rendimento restano visibili, senza scegliere una fonte in conflitto.
export function DsResultWarnings({teams}) {
	const issues = (teams || []).flatMap((team) =>
		(team.resultIssues || []).map((match) => ({...match, team})),
	);
	if (!issues.length) return null;
	return (
		<div className="ds-missing" role="status">
			Risultati da verificare, esclusi dal rendimento:
			<ul>
				{issues.map((match) => (
					<li key={match.team.id + match.id}>
						{match.team.name} · {moment(match.startDate).format("D/MM")} ·{" "}
						{match.warning}{" "}
						<DsLink to={"/matches/" + match.id}>Gara completa</DsLink>
					</li>
				))}
			</ul>
		</div>
	);
}

// Una sola riga per tutto ciò che manca, con il reparto che deve inserirlo
export function DsMissing({items}) {
	if (!items?.length) return null;
	return (
		<div className="ds-missing-data">
			<span className="ds-missing-data__label">Ancora da inserire</span>
			{items.map((item) => (
				<span key={item.label} className="ds-missing-data__item">
					{item.label}
					{item.owner && <small> · {item.owner}</small>}
				</span>
			))}
		</div>
	);
}

export function DsLink({to, children = "Dettaglio"}) {
	const navigate = useNavigate();
	return (
		<button className="ds-link" onClick={() => navigate(to)}>
			{children}
			<ChevronRightIcon fontSize="small" />
		</button>
	);
}

export function DsForm({form}) {
	if (!form?.length) return <span className="ds-muted">—</span>;
	return (
		<span className="ds-form">
			{form.map((r, i) => (
				<span key={i} className={"ds-form__chip ds-form__chip--" + r}>
					{r}
				</span>
			))}
		</span>
	);
}

export function DsBar({value, max = 1, tone = "primary"}) {
	const pct = max ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
	return (
		<span className="ds-bar">
			<span
				className={"ds-bar__fill ds-bar__fill--" + tone}
				style={{width: pct + "%"}}
			/>
		</span>
	);
}

export const DsTable = DataTable;

// Segnaposto durante il primo caricamento: evita lo sfondo scuro a tutta pagina
export function DsSkeleton({visible, sections = 2}) {
	if (!visible) return null;
	return (
		<div className="ds-skeleton" aria-busy="true" aria-label="Caricamento">
			<div className="kpi-row">
				{[0, 1, 2, 3].map((i) => (
					<div key={i} className="ds-skeleton__block ds-skeleton__block--kpi" />
				))}
			</div>
			{Array.from({length: sections}).map((_, i) => (
				<div
					key={i}
					className="ds-skeleton__block ds-skeleton__block--section"
				/>
			))}
		</div>
	);
}

// Anello 0-100 per l'indice di salute del club
export function DsRing({value, size = 96, stroke = 9, tone = "green"}) {
	const radius = (size - stroke) / 2;
	const circumference = 2 * Math.PI * radius;
	const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
	return (
		<svg
			className={"ds-ring ds-ring--" + tone}
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
		>
			<circle
				className="ds-ring__track"
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={stroke}
			/>
			<circle
				className="ds-ring__value"
				cx={size / 2}
				cy={size / 2}
				r={radius}
				strokeWidth={stroke}
				strokeDasharray={circumference}
				strokeDashoffset={circumference * (1 - pct / 100)}
				transform={`rotate(-90 ${size / 2} ${size / 2})`}
			/>
			<text
				className="ds-ring__label"
				x="50%"
				y="50%"
				dominantBaseline="central"
				textAnchor="middle"
			>
				{value == null ? "—" : Math.round(value)}
			</text>
		</svg>
	);
}

export const DEPARTMENT_ICONS = {
	direzione: WorkIcon,
	tecnica: SportsIcon,
	performance: MonitorHeartIcon,
	medica: LocalHospitalIcon,
	scouting: TravelExploreIcon,
	segreteria: AssignmentIcon,
	amministrazione: AccountBalanceIcon,
	teammanager: AssignmentTurnedInIcon,
	strutture: StadiumIcon,
	commerciale: HandshakeIcon,
};

export const DsChips = ChoiceChips;

const relativeDays = (date) => {
	const days = moment(date)
		.startOf("day")
		.diff(moment().startOf("day"), "days");
	return days;
};

export function DsActivity({item, seen, onToggleSeen, onOpen}) {
	const Icon = DEPARTMENT_ICONS[item.department];
	const opened = item.openedAt ? -relativeDays(item.openedAt) : null;
	const due = item.dueAt ? relativeDays(item.dueAt) : null;
	return (
		<div
			className={
				"ds-activity ds-activity--" + item.severity + (seen ? " is-seen" : "")
			}
		>
			<span className="ds-activity__icon">
				{Icon && <Icon fontSize="small" />}
			</span>
			<div
				className={"ds-activity__body" + (onOpen ? " is-clickable" : "")}
				onClick={onOpen}
			>
				<div className="ds-activity__title">
					{item.title} {item.preview && <DsPreviewBadge />}{" "}
					{item.experimental && (
						<span className="ds-preview-badge">Euristica DS</span>
					)}
				</div>
				{item.detail && (
					<div className="ds-activity__detail">{item.detail}</div>
				)}
				<div className="ds-activity__meta">
					{opened !== null && opened > 0 && <span>Aperta da {opened} gg</span>}
					{due !== null && (
						<span className={due < 0 ? "is-late" : due <= 7 ? "is-soon" : ""}>
							{due < 0
								? `Scaduta da ${-due} gg`
								: due === 0
									? "Scade oggi"
									: `Scadenza tra ${due} gg`}
						</span>
					)}
				</div>
			</div>
			{onToggleSeen && (
				<button
					className={"ds-activity__seen" + (seen ? " is-on" : "")}
					onClick={onToggleSeen}
					title={seen ? "Riporta tra le aperte" : "Segna come presa in visione"}
				>
					<CheckIcon fontSize="small" />
					<span>{seen ? "Vista" : "Presa visione"}</span>
				</button>
			)}
		</div>
	);
}

export const dayLabel = (date) => {
	const days = relativeDays(date);
	if (days === 0) return "Oggi";
	if (days === -1) return "Ieri";
	if (days === 1) return "Domani";
	return moment(date).format("dddd D MMMM");
};

export function DsTimelineEvent({event, onOpen, compact}) {
	const Icon = DEPARTMENT_ICONS[event.department];
	const date = moment(event.date);
	const hasTime = date.hours() !== 0 || date.minutes() !== 0;
	return (
		<div
			className={
				"ds-event ds-event--" +
				(event.tone || "neutral") +
				(date.isBefore(moment()) &&
				!(event.startTimeToBeConfirmed && date.isSame(moment(), "day"))
					? " is-past"
					: "") +
				(compact ? " is-compact" : "") +
				(onOpen ? " is-clickable" : "")
			}
			onClick={onOpen}
		>
			<span className="ds-event__time">
				{event.startTimeToBeConfirmed
					? "OdD"
					: hasTime
						? date.format("HH:mm")
						: ""}
			</span>
			<span className="ds-event__dot">
				{Icon && <Icon fontSize="inherit" />}
			</span>
			<div className="ds-event__body">
				<div className="ds-event__title">
					{event.title} {event.preview && <DsPreviewBadge />}
				</div>
				{event.detail && <div className="ds-event__detail">{event.detail}</div>}
			</div>
		</div>
	);
}

// Filtro squadra condiviso da tutte le pagine DS (null = tutte le squadre)
export function DsTeamSelect({scope, dark}) {
	const [anchor, setAnchor] = useState(null);
	if (!scope?.teams?.length) return null;
	const options = [{id: null, name: "Tutte le squadre"}, ...scope.teams];
	const current = options.find((t) => t.id === (scope.teamId || null));
	const choose = (id) => {
		scope.setTeamId(id);
		setAnchor(null);
	};
	return (
		<>
			<button
				type="button"
				className={"ds-team-select" + (dark ? " ds-team-select--dark" : "")}
				onClick={(e) => setAnchor(e.currentTarget)}
				aria-haspopup="listbox"
			>
				<span className="ds-team-select__label">Squadra</span>
				<span className="ds-team-select__value">
					{current?.name || "Tutte le squadre"}
				</span>
				<ExpandMoreIcon fontSize="small" />
			</button>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={() => setAnchor(null)}
			>
				{options.map((t) => (
					<MenuItem
						key={t.id || "all"}
						selected={t.id === (scope.teamId || null)}
						onClick={() => choose(t.id)}
					>
						{t.name}
					</MenuItem>
				))}
			</Menu>
		</>
	);
}

// Chi alimenta i dati dell'area e se ha già un'utenza
export function DsOwners({owners}) {
	const navigate = useNavigate();
	if (!owners) return null;
	const withAccount = owners.people.filter((p) => p.hasAccount).length;
	return (
		<div className="ds-owners" onClick={() => navigate("/ds/staff")}>
			<span className="ds-owners__label">
				Ruoli associabili · {owners.label}:
			</span>{" "}
			{owners.people.length ? (
				<>
					{owners.people
						.slice(0, 4)
						.map((p) => `${p.firstName} ${p.lastName}`)
						.join(", ")}
					{owners.people.length > 4 && ` +${owners.people.length - 4}`}
					<span
						className={
							"ds-owners__accounts" + (withAccount ? "" : " is-missing")
						}
					>
						{withAccount}/{owners.people.length} associabili per nome
					</span>
				</>
			) : (
				<span className="ds-owners__accounts is-missing">
					nessuna corrispondenza nei ruoli consultati
				</span>
			)}
		</div>
	);
}

// ── Ritorno alla vista DS dalle pagine della vista completa ────────────────────

const DS_PARENT_ROUTES = [
	["/manager/players-contracts", "/ds/mercato", "Mercato & contratti"],
	["/manager/market-simulation", "/ds/mercato", "Mercato & contratti"],
	["/manager/finance", "/ds/economia", "Economia"],
	["/manager/staffs", "/ds/staff", "Staff"],
	["/manager/teams", "/ds/squadre", "Squadre"],
	["/manager/players-comparison", "/ds/squadre", "Squadre"],
	["/players", "/ds/squadre", "Squadre"],
	["/match-day", "/ds/team-manager", "Team manager"],
	["/matches", "/ds/gare", "Gare"],
	["/calendar", "/ds/gare", "Gare"],
	["/competitions", "/ds/gare", "Gare"],
	["/match-reports", "/ds/gare", "Gare"],
	["/trainings", "/ds/team-manager", "Team manager"],
	["/measurations", "/ds/performance", "Performance"],
	["/gps", "/ds/performance", "Performance"],
	["/athletic-tests", "/ds/performance", "Performance"],
	["/medical", "/ds/medica", "Area medica"],
	["/scouting", "/ds/scouting", "Scouting"],
	["/development", "/ds/scouting", "Scouting"],
	["/players-valuations", "/ds/scouting", "Scouting"],
	["/secretariat", "/ds/segreteria", "Segreteria"],
];

// Schede di dettaglio con una sintesi DS dedicata
const DS_SUMMARY_ROUTES = [
	[/^\/players\/([^/]+)$/, "/ds/giocatore/", "Sintesi giocatore"],
	[/^\/manager\/teams\/([^/]+)$/, "/ds/squadra/", "Sintesi squadra"],
	[/^\/matches\/([^/]+)$/, "/ds/gara/", "Sintesi gara"],
];

// Pagina DS "madre" della pagina corrente (null fuori dalla vista DS)
export const useDsReturnTarget = () => {
	const location = useLocation();
	const menu = useSelector((state) => state?.selectedMenuItem);
	if (menu !== "ds" || location.pathname.startsWith("/ds")) return null;
	for (const [pattern, base, label] of DS_SUMMARY_ROUTES) {
		const match = location.pathname.match(pattern);
		if (match && !["new"].includes(match[1]))
			return {path: base + match[1], label};
	}
	const [, path, label] = DS_PARENT_ROUTES.find(([prefix]) =>
		location.pathname.startsWith(prefix),
	) || [null, "/ds/oggi", "Il club oggi"];
	return {path, label};
};

export function DsReturnBar({target}) {
	const navigate = useNavigate();
	if (!target) return null;
	const {path, label} = target;
	return (
		<div className="ds-return">
			<button className="ds-return__back" onClick={() => navigate(path)}>
				<ArrowBackIcon fontSize="small" /> Torna a {label}
			</button>
			<span className="ds-return__hint">
				Stai consultando il dettaglio nella vista completa
			</span>
			{path !== "/ds/oggi" && (
				<button
					className="ds-return__home"
					onClick={() => navigate("/ds/oggi")}
				>
					Il club oggi
				</button>
			)}
		</div>
	);
}

// Navigazione rapida su telefono (solo vista DS, nascosta su desktop)
const BOTTOM_NAV = [
	{path: "/ds/oggi", label: "Oggi", Icon: TodayIcon},
	{path: "/ds/attivita", label: "Attività", Icon: InboxIcon},
	{path: "/ds/timeline", label: "Linea temporale", Icon: TimelineIcon},
	{
		path: "/ds/squadre",
		label: "Squadre",
		Icon: GroupsIcon,
		also: "/ds/squadra",
	},
];

export function DsBottomNav() {
	const navigate = useNavigate();
	const location = useLocation();
	return (
		<nav className="ds-bottom-nav">
			{BOTTOM_NAV.map(({path, label, Icon, also}) => {
				const active =
					location.pathname.startsWith(path) ||
					(also && location.pathname.startsWith(also));
				return (
					<button
						key={path}
						className={"ds-bottom-nav__item" + (active ? " is-active" : "")}
						onClick={() => navigate(path)}
					>
						<Icon fontSize="small" />
						<span>{label}</span>
					</button>
				);
			})}
		</nav>
	);
}
